import type { ReactiveController, ReactiveControllerHost } from 'lit';
import type {
  DatePickerValue,
  DatePickerRangeValue,
  DatePickerMultipleValue,
  DateSelectionMode,
} from '../types.js';

// ─────────────────────────────────────────────────────────────────────────────
// FormatHost
// ─────────────────────────────────────────────────────────────────────────────

export interface FormatHost extends ReactiveControllerHost {
  dateFormat:    string;
  locale:        string;
  selectionMode: DateSelectionMode;
}

// ─────────────────────────────────────────────────────────────────────────────
// FormatController
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Handles date ↔ string conversion using PrimeNG-compatible format tokens.
 *
 * Token reference:
 *  d   — day of month, no leading zero           (1)
 *  dd  — day of month, two digits                (01)
 *  o   — day of year, no leading zeros           (1)
 *  oo  — day of year, three digits               (001)
 *  D   — day name short                          (Mon)
 *  DD  — day name long                           (Monday)
 *  m   — month of year, no leading zero          (1)
 *  mm  — month of year, two digits               (01)
 *  M   — month name short                        (Jan)
 *  MM  — month name long                         (January)
 *  y   — two-digit year                          (25)
 *  yy  — four-digit year                         (2025)
 *  @   — Unix timestamp in ms                    (1735689600000)
 *  !   — Windows ticks (100ns since 01/01/0001)
 *  '…' — literal text (single-quoted)
 *  ''  — single-quote literal
 */
export class FormatController implements ReactiveController {
  readonly host: FormatHost;

  constructor(host: FormatHost) {
    this.host = host;
    host.addController(this);
  }

  hostConnected(): void { /* no-op */ }
  hostDisconnected(): void { /* no-op */ }

  // ── Public API ───────────────────────────────────────────────────

  /** Format a single Date to the configured dateFormat string. */
  format(date: Date): string {
    return this._applyFormat(date, this.host.dateFormat);
  }

  /** Parse a string back to a Date using the configured dateFormat. Returns null on failure. */
  parse(text: string): Date | null {
    return this._parseFormat(text.trim(), this.host.dateFormat);
  }

  /**
   * Format a DatePickerValue to a display string for the input trigger.
   * Range: "start – end" (or just start when end is null).
   * Multiple: comma-separated dates.
   */
  formatInputValue(value: DatePickerValue): string {
    const mode = this.host.selectionMode;
    if (mode === 'single') {
      return value ? this.format(value as Date) : '';
    }
    if (mode === 'range') {
      const r = value as DatePickerRangeValue;
      if (!r) return '';
      const start = this.format(r[0]);
      return r[1] ? `${start} – ${this.format(r[1])}` : start;
    }
    if (mode === 'multiple') {
      const arr = (value as DatePickerMultipleValue) ?? [];
      return arr.map(d => this.format(d)).join(', ');
    }
    return '';
  }

  /** Returns a human-readable placeholder derived from the format string, e.g. mm/dd/yyyy. */
  getInputPlaceholder(): string {
    return this.host.dateFormat
      .replace(/yy/g, 'yyyy')
      .replace(/y\b/g, 'yy')
      .replace(/mm/g, 'mm')
      .replace(/m\b/g, 'm')
      .replace(/dd/g, 'dd')
      .replace(/d\b/g, 'd')
      .replace(/'[^']*'/g, '')
      .trim();
  }

  // ── Format engine ─────────────────────────────────────────────────

  private _applyFormat(date: Date, fmt: string): string {
    // Special single-token formats
    if (fmt === '@') return String(date.getTime());
    if (fmt === '!') return String(date.getTime() * 10000 + 621355968000000000);

    const tokens = this._tokenize(fmt);
    return tokens
      .map(tok => {
        if (tok.type === 'literal') return tok.value;
        return this._formatToken(tok.value, date);
      })
      .join('');
  }

  private _formatToken(token: string, d: Date): string {
    switch (token) {
      case 'dd': return String(d.getDate()).padStart(2, '0');
      case 'd':  return String(d.getDate());
      case 'oo': return String(this._dayOfYear(d)).padStart(3, '0');
      case 'o':  return String(this._dayOfYear(d));
      case 'DD': return d.toLocaleString('en', { weekday: 'long' });
      case 'D':  return d.toLocaleString('en', { weekday: 'short' });
      case 'mm': return String(d.getMonth() + 1).padStart(2, '0');
      case 'm':  return String(d.getMonth() + 1);
      case 'MM': return d.toLocaleString('en', { month: 'long' });
      case 'M':  return d.toLocaleString('en', { month: 'short' });
      case 'yy': return String(d.getFullYear());
      case 'y':  return String(d.getFullYear()).slice(-2);
      case '@':  return String(d.getTime());
      case '!':  return String(d.getTime() * 10000 + 621355968000000000);
      default:   return token;
    }
  }

  // ── Parse engine ─────────────────────────────────────────────────

  private _parseFormat(text: string, fmt: string): Date | null {
    if (!text) return null;

    // Unix timestamp
    if (fmt === '@') {
      const ms = parseInt(text, 10);
      return isNaN(ms) ? null : new Date(ms);
    }
    // Windows ticks
    if (fmt === '!') {
      const ticks = parseInt(text, 10);
      return isNaN(ticks) ? null : new Date((ticks - 621355968000000000) / 10000);
    }

    const tokens = this._tokenize(fmt);
    let pos  = 0;
    let day  = 1, month = 0, year = new Date().getFullYear();

    for (const tok of tokens) {
      if (tok.type === 'literal') {
        // Skip the literal characters in the input
        pos += tok.value.length;
        continue;
      }
      switch (tok.value) {
        case 'dd':
        case 'd': {
          const n = this._readInt(text, pos, 2);
          if (n === null) return null;
          day = n.value; pos = n.end; break;
        }
        case 'mm':
        case 'm': {
          const n = this._readInt(text, pos, 2);
          if (n === null) return null;
          month = n.value - 1; pos = n.end; break;
        }
        case 'yy': {
          const n = this._readInt(text, pos, 4);
          if (n === null) return null;
          year = n.value; pos = n.end; break;
        }
        case 'y': {
          const n = this._readInt(text, pos, 2);
          if (n === null) return null;
          const century = Math.floor(new Date().getFullYear() / 100) * 100;
          year = century + n.value; pos = n.end; break;
        }
        default:
          // Skip unrecognised parse tokens (D, DD, M, MM, o, oo)
          break;
      }
    }

    if (isNaN(day) || isNaN(month) || isNaN(year)) return null;
    const result = new Date(year, month, day);
    if (result.getMonth() !== month || result.getDate() !== day) return null; // invalid date
    return result;
  }

  // ── Tokenizer ─────────────────────────────────────────────────────

  private _tokenize(fmt: string): { type: 'token' | 'literal'; value: string }[] {
    const result: { type: 'token' | 'literal'; value: string }[] = [];
    let i = 0;

    while (i < fmt.length) {
      const ch = fmt[i];

      // Single-quoted literal — '...' or '' (escaped single quote)
      if (ch === "'") {
        if (fmt[i + 1] === "'") {
          result.push({ type: 'literal', value: "'" });
          i += 2;
        } else {
          let literal = '';
          i++;
          while (i < fmt.length && fmt[i] !== "'") {
            literal += fmt[i++];
          }
          i++; // consume closing quote
          result.push({ type: 'literal', value: literal });
        }
        continue;
      }

      // Multi-char tokens (longest-match first)
      const twoChar = fmt.slice(i, i + 2);
      if (['dd', 'oo', 'DD', 'mm', 'MM', 'yy'].includes(twoChar)) {
        result.push({ type: 'token', value: twoChar });
        i += 2;
        continue;
      }

      // Single-char tokens
      if (['d', 'o', 'D', 'm', 'M', 'y', '@', '!'].includes(ch)) {
        result.push({ type: 'token', value: ch });
        i++;
        continue;
      }

      // Plain literal character
      result.push({ type: 'literal', value: ch });
      i++;
    }

    return result;
  }

  // ── Helpers ───────────────────────────────────────────────────────

  private _dayOfYear(d: Date): number {
    const start = new Date(d.getFullYear(), 0, 0);
    return Math.floor((d.getTime() - start.getTime()) / 86400000);
  }

  private _readInt(str: string, pos: number, maxLen: number): { value: number; end: number } | null {
    let end = pos;
    while (end < str.length && end - pos < maxLen && /\d/.test(str[end])) end++;
    if (end === pos) return null;
    return { value: parseInt(str.slice(pos, end), 10), end };
  }
}
